import React from 'react';
import type {
  AdminLicenseCreateRequest,
  AdminLicenseDetailDto,
  AdminLicensePatchRequest,
  AdminLicenseSaveResult,
  AdminMutationOutcome,
} from '../types';
import { getAdminApiError } from '../lib/adminApiErrors';
import {
  createLicenseCreateRequest,
  createLicenseDraft,
  createLicensePatchRequest,
  createNewLicenseDraft,
  isLicenseDraftDirty,
  togglePendingMachineRevoke,
  validateLicenseDraft,
  type LicenseDraft,
} from '../lib/adminLicenses';
import { useAdminToast } from '../ui/components';

export interface LicenseDraftSession {
  draft: LicenseDraft;
  dirty: boolean;
  pendingRevokeIds: number[];
  saveError: string | null;
  serverLimitError: string | null;
  validation: ReturnType<typeof validateLicenseDraft>;
  createPatch: () => AdminLicensePatchRequest | null;
  setField: <Key extends keyof LicenseDraft>(key: Key, value: LicenseDraft[Key]) => void;
  toggleRevoke: (machineId: number) => void;
  reconcile: (result: AdminLicenseSaveResult) => boolean;
  setSaveError: (message: string | null) => void;
}

export const useLicenseDraftSession = (
  open: boolean,
  mode: 'create' | 'edit',
  license: AdminLicenseDetailDto | null,
  firstCompanyId: number
): LicenseDraftSession => {
  const licenseId = license?.id ?? null;
  const initialLicenseId = React.useRef<number | null>(null);
  const initialLicense = React.useRef<AdminLicenseDetailDto | null>(null);
  if (licenseId !== initialLicenseId.current) {
    initialLicenseId.current = licenseId;
    initialLicense.current = license;
  }
  const [draft, setDraft] = React.useState(() => createNewLicenseDraft(firstCompanyId));
  const [original, setOriginal] = React.useState(draft);
  const [pendingRevokeIds, setPendingRevokeIds] = React.useState<number[]>([]);
  const [serverLimitError, setServerLimitError] = React.useState<string | null>(null);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    const canonical = initialLicense.current;
    const initial = mode === 'edit' && canonical
      ? createLicenseDraft(canonical)
      : createNewLicenseDraft(firstCompanyId);
    setDraft(initial);
    setOriginal(initial);
    setPendingRevokeIds([]);
    setServerLimitError(null);
    setSaveError(null);
  }, [firstCompanyId, licenseId, mode, open]);

  const setField = <Key extends keyof LicenseDraft>(key: Key, value: LicenseDraft[Key]) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setSaveError(null);
    if (key === 'allowedInstances') {
      setServerLimitError(null);
    }
  };
  const reconcile = (result: AdminLicenseSaveResult) => {
    const canonical = createLicenseDraft(result.canonical);
    setOriginal(canonical);
    setPendingRevokeIds(result.revokeFailures.map((failure) => failure.id));
    if (result.patchSucceeded) {
      setDraft(canonical);
    }
    if (result.patchError?.code === 'allowed_instances_below_active') {
      const count = result.canonical.machinesCount;
      setServerLimitError(
        `Allowed instances cannot be below the ${count} active ${count === 1 ? 'machine' : 'machines'}.`
      );
    } else if (result.patchError) {
      setSaveError(result.patchError.message);
    }
    return result.patchError !== null
      || result.revokeFailures.length > 0
      || result.refreshError !== null;
  };

  return {
    draft,
    dirty: isLicenseDraftDirty(draft, original, pendingRevokeIds),
    pendingRevokeIds,
    saveError,
    serverLimitError,
    validation: validateLicenseDraft(draft),
    createPatch: () => createLicensePatchRequest(draft, original),
    setField,
    toggleRevoke: (machineId) => setPendingRevokeIds((ids) =>
      togglePendingMachineRevoke(ids, machineId)),
    reconcile,
    setSaveError,
  };
};

interface DirtyCloseOptions {
  blocked: boolean;
  dirty: boolean;
  onClose: () => void;
}

export const useLicenseDirtyClose = ({ blocked, dirty, onClose }: DirtyCloseOptions) => {
  const [discardOpen, setDiscardOpen] = React.useState(false);
  const resolver = React.useRef<((discard: boolean) => void) | null>(null);
  const resolveDiscard = (discard: boolean) => {
    setDiscardOpen(false);
    resolver.current?.(discard);
    resolver.current = null;
  };
  const confirmDirtyClose = () => {
    if (blocked || discardOpen) {
      return Promise.resolve(false);
    }
    setDiscardOpen(true);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  };
  const requestCancel = async () => {
    if (dirty && !await confirmDirtyClose()) {
      return;
    }
    onClose();
  };
  return { confirmDirtyClose, discardOpen, requestCancel, resolveDiscard };
};

const machineLabel = (license: AdminLicenseDetailDto | null, id: number): string =>
  license?.machines.find((machine) => machine.id === id)?.host ?? `machine ${id}`;

const saveSummary = (
  patchRequested: boolean,
  result: AdminLicenseSaveResult,
  license: AdminLicenseDetailDto | null
): string => {
  const messages: string[] = [];
  if (patchRequested) {
    messages.push(result.patchSucceeded
      ? 'License fields saved.'
      : `License fields failed (${result.patchError?.code ?? 'license_update_failed'}).`);
  }
  if (result.revokedMachineIds.length > 0) {
    messages.push(`Revoked ${result.revokedMachineIds
      .map((id) => machineLabel(license, id)).join(', ')}.`);
  }
  result.revokeFailures.forEach((failure) => messages.push(
    `Revoke failed for ${machineLabel(license, failure.id)} (${failure.code}).`
  ));
  if (result.refreshError) {
    messages.push(`Canonical refresh failed (${result.refreshError.code}).`);
  }
  return messages.join(' ');
};

interface MutationOptions {
  mode: 'create' | 'edit';
  license: AdminLicenseDetailDto | null;
  loading: boolean;
  session: LicenseDraftSession;
  onClose: () => void;
  onCreate: (request: AdminLicenseCreateRequest) => Promise<AdminLicenseDetailDto>;
  onSave: (
    id: number,
    patch: AdminLicensePatchRequest | null,
    pendingRevokeIds: readonly number[]
  ) => Promise<AdminLicenseSaveResult>;
  onDelete: (id: number) => Promise<AdminMutationOutcome>;
}

export const useLicenseMutations = (options: MutationOptions) => {
  const { showToast } = useAdminToast();
  const [submitting, setSubmitting] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const canSubmit = Object.keys(options.session.validation).length === 0
    && !submitting && !options.loading
    && (options.mode === 'create' || options.session.dirty);
  const copyKey = async () => {
    if (!options.license?.activationKey || !navigator.clipboard) {
      showToast('The activation key could not be copied.', { tone: 'critical' });
      return;
    }
    try {
      await navigator.clipboard.writeText(options.license.activationKey);
      showToast('Activation key copied.');
    } catch {
      showToast('The activation key could not be copied.', { tone: 'critical' });
    }
  };
  const submit = async () => {
    if (!canSubmit) {
      return;
    }
    setSubmitting(true);
    options.session.setSaveError(null);
    try {
      if (options.mode === 'create') {
        const created = await options.onCreate(createLicenseCreateRequest(options.session.draft));
        showToast(`License created: ${created.activationKey}.`);
        options.onClose();
      } else if (options.license) {
        const patch = options.session.createPatch();
        const result = await options.onSave(
          options.license.id,
          patch,
          options.session.pendingRevokeIds
        );
        const partial = options.session.reconcile(result);
        if (partial) {
          showToast(saveSummary(patch !== null, result, options.license), { tone: 'warning' });
        } else {
          showToast(result.revokedMachineIds.length > 0
            ? `License saved; revoked ${result.revokedMachineIds.length} machine(s).`
            : 'License saved.');
          options.onClose();
        }
      }
    } catch (error) {
      const failure = getAdminApiError(error, 'license_save_failed');
      options.session.setSaveError(failure.message);
      showToast(`License save failed (${failure.code}).`, { tone: 'critical' });
    } finally {
      setSubmitting(false);
    }
  };
  const confirmDelete = async () => {
    if (!options.license) {
      return;
    }
    setSubmitting(true);
    try {
      const result = await options.onDelete(options.license.id);
      setDeleteOpen(false);
      showToast(result.refreshError
        ? `License deleted; canonical refresh failed (${result.refreshError.code}).`
        : 'License deleted.', result.refreshError ? { tone: 'warning' } : undefined);
      options.onClose();
    } catch (error) {
      const failure = getAdminApiError(error, 'license_delete_failed');
      showToast(`License delete failed (${failure.code}).`, { tone: 'critical' });
    } finally {
      setSubmitting(false);
    }
  };
  return { canSubmit, confirmDelete, copyKey, deleteOpen, setDeleteOpen, submit, submitting };
};
